import { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import {
  getAnboxStats,
  getAnboxMachines,
  getAnboxInstances,
  createAnboxMachine,
  updateAnboxMachine,
  deleteAnboxMachine,
  updateAnboxMachineStatus,
  addInstancesToMachine,
  deleteAnboxInstance,
  updateAnboxInstanceStatus,
  allocateInstance,
  releaseInstance,
  getMachineCredentials,
  AnboxStatsResponse,
  AnboxMachine,
  AnboxInstance,
  AnboxMachinesQueryParams,
  AnboxInstancesQueryParams,
  CreateAnboxMachineRequest,
  AnboxMachineCredentials
} from 'services/admin/superAdminService';
import Spinner from 'components/common/Spinner';
import { Cloud, RefreshCcw, Plus, Gauge, HardDrive, Box, CheckCircle2, AlertTriangle, XCircle, Loader2, Trash2, Pencil, PlusCircle, Eye, EyeOff, Key, Inbox, X, BarChart3, Wrench, Clipboard, ArrowRightFromLine, Menu } from 'lucide-react';
import { useSidebar } from 'contexts/SidebarContext';

type TabType = 'stats' | 'machines' | 'instances';

export default function AnboxManagement() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Theme tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableBorderColor = isDark ? '#1E293B' : '#E2E8F0';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#1E293B';
  const buttonBg = isDark ? '#1D293D' : '#F3F4F6';
  const buttonText = isDark ? '#90A1B9' : '#374151';
  const paginationBg = isDark ? '#1D293D' : '#FFFFFF';
  const paginationBorder = isDark ? '#314158' : '#D1D5DB';
  const paginationText = isDark ? '#90A1B9' : '#374151';
  const successBg = isDark ? '#0D2818' : '#F0FDF4';
  const successBorder = isDark ? '#1A4028' : '#BBF7D0';
  const successText = isDark ? '#5EE9B5' : '#166534';
  const errorBg = isDark ? '#2A1215' : '#FEF2F2';
  const errorBorder = isDark ? '#4A1C20' : '#FECACA';
  const errorText = isDark ? '#F87171' : '#991B1B';
  const modalBackdrop = isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)';
  const modalBg = isDark ? '#1D293D' : '#FFFFFF';
  const modalInfoBg = isDark ? '#111827' : '#F9FAFB';

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('stats');

  // Stats state
  const [stats, setStats] = useState<AnboxStatsResponse | null>(null);

  // Machines state
  const [machines, setMachines] = useState<AnboxMachine[]>([]);
  const [machinesPagination, setMachinesPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [machineStatusFilter, setMachineStatusFilter] = useState<string>('');

  // Instances state
  const [instances, setInstances] = useState<AnboxInstance[]>([]);
  const [instancesPagination, setInstancesPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [instanceStatusFilter, setInstanceStatusFilter] = useState<string>('');
  const [instanceMachineFilter, setInstanceMachineFilter] = useState<string>('');

  // Modal states
  const [showAddMachineModal, setShowAddMachineModal] = useState(false);
  const [showEditMachineModal, setShowEditMachineModal] = useState(false);
  const [showDeleteMachineModal, setShowDeleteMachineModal] = useState(false);
  const [showAddInstancesModal, setShowAddInstancesModal] = useState(false);
  const [showInstanceActionModal, setShowInstanceActionModal] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<AnboxMachine | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<AnboxInstance | null>(null);
  const [instanceAction, setInstanceAction] = useState<'release' | 'delete' | 'status'>('release');
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [machineForm, setMachineForm] = useState<CreateAnboxMachineRequest>({
    name: '',
    host: '',
    port: 8443,
    api_endpoint: '',
    auth_token: '',
    root_username: '',
    root_password: '',
    total_capacity: 50,
    warning_threshold: 80,
    critical_threshold: 95,
    priority: 1,
    region: '',
    provider: '',
    notes: ''
  });
  const [addInstancesCount, setAddInstancesCount] = useState(10);
  const [addInstancesPrefix, setAddInstancesPrefix] = useState('instance');
  const [newInstanceStatus, setNewInstanceStatus] = useState<string>('available');

  // Credentials modal state
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [machineCredentials, setMachineCredentials] = useState<AnboxMachineCredentials | null>(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAnboxStats();
      setStats(response);
    } catch (err: any) {
      console.error('Failed to fetch Anbox stats:', err);
      setError(err.message || 'Failed to fetch Anbox stats');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch machines
  const fetchMachines = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: AnboxMachinesQueryParams = {
        page: machinesPagination.page,
        limit: machinesPagination.limit,
        status: machineStatusFilter as any || undefined
      };
      const response = await getAnboxMachines(params);
      setMachines(response.machines || []);
      setMachinesPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0
      }));
    } catch (err: any) {
      console.error('Failed to fetch machines:', err);
      setError(err.message || 'Failed to fetch machines');
      setMachines([]);
    } finally {
      setLoading(false);
    }
  }, [machinesPagination.page, machinesPagination.limit, machineStatusFilter]);

  // Fetch instances
  const fetchInstances = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: AnboxInstancesQueryParams = {
        page: instancesPagination.page,
        limit: instancesPagination.limit,
        status: instanceStatusFilter || undefined,
        machineId: instanceMachineFilter || undefined
      };
      const response = await getAnboxInstances(params);
      setInstances(response.instances || []);
      setInstancesPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0
      }));
    } catch (err: any) {
      console.error('Failed to fetch instances:', err);
      setError(err.message || 'Failed to fetch instances');
      setInstances([]);
    } finally {
      setLoading(false);
    }
  }, [instancesPagination.page, instancesPagination.limit, instanceStatusFilter, instanceMachineFilter]);

  // Initial fetch
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Fetch data on tab change
  useEffect(() => {
    if (activeTab === 'stats') {
      fetchStats();
    } else if (activeTab === 'machines') {
      fetchMachines();
    } else if (activeTab === 'instances') {
      fetchInstances();
    }
  }, [activeTab, fetchStats, fetchMachines, fetchInstances]);

  // Handle create machine
  const handleCreateMachine = async () => {
    try {
      setActionLoading(true);
      await createAnboxMachine(machineForm);
      setSuccessMessage('Machine created successfully');
      setShowAddMachineModal(false);
      resetMachineForm();
      fetchMachines();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle update machine
  const handleUpdateMachine = async () => {
    if (!selectedMachine) return;
    try {
      setActionLoading(true);
      const updateData: any = {
        name: machineForm.name,
        total_capacity: machineForm.total_capacity,
        warning_threshold: machineForm.warning_threshold,
        critical_threshold: machineForm.critical_threshold,
        priority: machineForm.priority,
        region: machineForm.region,
        provider: machineForm.provider,
        notes: machineForm.notes
      };
      // Only include credentials if they were changed (not empty)
      if (machineForm.root_username) updateData.root_username = machineForm.root_username;
      if (machineForm.root_password) updateData.root_password = machineForm.root_password;
      if (machineForm.auth_token) updateData.auth_token = machineForm.auth_token;

      await updateAnboxMachine(selectedMachine._id, updateData);
      setSuccessMessage('Machine updated successfully');
      setShowEditMachineModal(false);
      setSelectedMachine(null);
      resetMachineForm();
      fetchMachines();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete machine
  const handleDeleteMachine = async (permanent: boolean = false) => {
    if (!selectedMachine) return;
    try {
      setActionLoading(true);
      await deleteAnboxMachine(selectedMachine._id, permanent);
      setSuccessMessage(permanent ? 'Machine permanently deleted' : 'Machine deleted successfully');
      setShowDeleteMachineModal(false);
      setSelectedMachine(null);
      fetchMachines();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle update machine status
  const handleUpdateMachineStatus = async (machineId: string, status: 'active' | 'maintenance' | 'offline') => {
    try {
      setActionLoading(true);
      await updateAnboxMachineStatus(machineId, status);
      setSuccessMessage(`Machine status updated to ${status}`);
      fetchMachines();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle add instances to machine
  const handleAddInstances = async () => {
    if (!selectedMachine) return;
    try {
      setActionLoading(true);
      const response = await addInstancesToMachine(selectedMachine._id, {
        count: addInstancesCount,
        instance_prefix: addInstancesPrefix
      });
      setSuccessMessage(response.message || `${addInstancesCount} instances added successfully`);
      setShowAddInstancesModal(false);
      setSelectedMachine(null);
      setAddInstancesCount(10);
      setAddInstancesPrefix('instance');
      fetchMachines();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle instance action
  const handleInstanceAction = async () => {
    if (!selectedInstance) return;
    try {
      setActionLoading(true);
      if (instanceAction === 'release') {
        await releaseInstance({ instance_id: selectedInstance._id });
        setSuccessMessage('Instance released successfully');
      } else if (instanceAction === 'delete') {
        await deleteAnboxInstance(selectedInstance._id);
        setSuccessMessage('Instance deleted successfully');
      } else if (instanceAction === 'status') {
        await updateAnboxInstanceStatus(selectedInstance._id, newInstanceStatus);
        setSuccessMessage(`Instance status updated to ${newInstanceStatus}`);
      }
      setShowInstanceActionModal(false);
      setSelectedInstance(null);
      fetchInstances();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Reset machine form
  const resetMachineForm = () => {
    setMachineForm({
      name: '',
      host: '',
      port: 8443,
      api_endpoint: '',
      auth_token: '',
      root_username: '',
      root_password: '',
      total_capacity: 50,
      warning_threshold: 80,
      critical_threshold: 95,
      priority: 1,
      region: '',
      provider: '',
      notes: ''
    });
  };

  // Open edit machine modal
  const openEditMachineModal = (machine: AnboxMachine) => {
    setSelectedMachine(machine);
    setMachineForm({
      name: machine.name,
      host: machine.host,
      port: machine.port,
      api_endpoint: machine.api_endpoint || '',
      auth_token: '', // Don't prefill credentials for security
      root_username: '',
      root_password: '',
      total_capacity: machine.total_capacity,
      warning_threshold: machine.warning_threshold,
      critical_threshold: machine.critical_threshold,
      priority: machine.priority,
      region: machine.region || '',
      provider: machine.provider || '',
      notes: machine.notes || ''
    });
    setShowEditMachineModal(true);
  };

  // Fetch and show machine credentials
  const handleViewCredentials = async (machine: AnboxMachine) => {
    try {
      setCredentialsLoading(true);
      setShowCredentialsModal(true);
      setSelectedMachine(machine);
      setShowPasswords(false);
      const credentials = await getMachineCredentials(machine._id);
      setMachineCredentials(credentials);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch credentials');
      setShowCredentialsModal(false);
    } finally {
      setCredentialsLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage(`${label} copied to clipboard`);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  // Format number
  const formatNumber = (value: number | undefined) => {
    if (value === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Get status badge style
  const getStatusBadgeStyle = (status: string): React.CSSProperties => {
    switch (status) {
      case 'active':
      case 'available':
      case 'running':
        return { background: isDark ? '#0D2818' : '#DCFCE7', color: isDark ? '#5EE9B5' : '#15803D' };
      case 'maintenance':
      case 'stopping':
      case 'starting':
        return { background: isDark ? '#2A2008' : '#FEF9C3', color: isDark ? '#FACC15' : '#A16207' };
      case 'offline':
      case 'stopped':
        return { background: isDark ? '#1D293D' : '#F3F4F6', color: isDark ? '#90A1B9' : '#475569' };
      case 'assigned':
        return { background: isDark ? '#0C1A2E' : '#DBEAFE', color: isDark ? '#60A5FA' : '#1D4ED8' };
      case 'error':
        return { background: isDark ? '#2A1215' : '#FEE2E2', color: isDark ? '#F87171' : '#DC2626' };
      default:
        return { background: isDark ? '#1D293D' : '#F3F4F6', color: isDark ? '#90A1B9' : '#475569' };
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Render Add Machine Modal
  const renderAddMachineModal = () => {
    if (!showAddMachineModal) return null;
    return ReactDOM.createPortal(
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
        <div className={`rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ background: modalBg }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: titleColor }}>{t('superAdmin.anbox.addMachine', 'Add New Machine')}</h3>
            <button onClick={() => { setShowAddMachineModal(false); resetMachineForm(); }} style={{ color: subtitleColor }}>
              <X size={18} />
            </button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.name', 'Name')} *</label>
                <input
                  type="text"
                  value={machineForm.name}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                  placeholder="anbox-machine-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.host', 'Host')} *</label>
                <input
                  type="text"
                  value={machineForm.host}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, host: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                  placeholder="10.0.0.1"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.port', 'Port')} *</label>
                <input
                  type="number"
                  value={machineForm.port}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.totalCapacity', 'Total Capacity')} *</label>
                <input
                  type="number"
                  value={machineForm.total_capacity}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, total_capacity: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.apiEndpoint', 'API Endpoint')}</label>
              <input
                type="text"
                value={machineForm.api_endpoint}
                onChange={(e) => setMachineForm(prev => ({ ...prev, api_endpoint: e.target.value }))}
                className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                placeholder="https://10.0.0.1:8443/api"
              />
            </div>
            {/* Credentials Section */}
            <div className="pt-4 mt-4" style={{ borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: tableBorderColor }}>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: titleColor }}>
                <Key size={16} style={{ color: '#818CF8' }} />
                {t('superAdmin.anbox.form.credentials', 'Credentials')}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.rootUsername', 'Root Username')}</label>
                  <input
                    type="text"
                    value={machineForm.root_username || ''}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, root_username: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                    placeholder="root"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.rootPassword', 'Root Password')}</label>
                  <input
                    type="password"
                    value={machineForm.root_password || ''}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, root_password: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.authToken', 'Auth Token')}</label>
                <input
                  type="password"
                  value={machineForm.auth_token || ''}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, auth_token: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                  placeholder="••••••••"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.warningThreshold', 'Warning %')}</label>
                <input
                  type="number"
                  value={machineForm.warning_threshold}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, warning_threshold: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.criticalThreshold', 'Critical %')}</label>
                <input
                  type="number"
                  value={machineForm.critical_threshold}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, critical_threshold: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.priority', 'Priority')}</label>
                <input
                  type="number"
                  value={machineForm.priority}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.region', 'Region')}</label>
                <input
                  type="text"
                  value={machineForm.region}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, region: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                  placeholder="us-east-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.provider', 'Provider')}</label>
                <input
                  type="text"
                  value={machineForm.provider}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, provider: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                  placeholder="aws"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.notes', 'Notes')}</label>
              <textarea
                value={machineForm.notes}
                onChange={(e) => setMachineForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                rows={2}
                placeholder="Optional notes about this machine"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => { setShowAddMachineModal(false); resetMachineForm(); }}
              className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
              style={{ background: buttonBg, color: buttonText }}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleCreateMachine}
              disabled={actionLoading || !machineForm.name || !machineForm.host}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-500 rounded-md hover:bg-indigo-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading && <Loader2 size={14} className="animate-spin" />}
              {t('superAdmin.anbox.createMachine', 'Create Machine')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render Edit Machine Modal
  const renderEditMachineModal = () => {
    if (!showEditMachineModal || !selectedMachine) return null;
    return ReactDOM.createPortal(
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
        <div className={`rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ background: modalBg }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: titleColor }}>{t('superAdmin.anbox.editMachine', 'Edit Machine')}</h3>
            <button onClick={() => { setShowEditMachineModal(false); setSelectedMachine(null); resetMachineForm(); }} style={{ color: subtitleColor }}>
              <X size={18} />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.name', 'Name')}</label>
              <input
                type="text"
                value={machineForm.name}
                onChange={(e) => setMachineForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.totalCapacity', 'Capacity')}</label>
                <input
                  type="number"
                  value={machineForm.total_capacity}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, total_capacity: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.warningThreshold', 'Warning %')}</label>
                <input
                  type="number"
                  value={machineForm.warning_threshold}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, warning_threshold: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.criticalThreshold', 'Critical %')}</label>
                <input
                  type="number"
                  value={machineForm.critical_threshold}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, critical_threshold: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.priority', 'Priority')}</label>
                <input
                  type="number"
                  value={machineForm.priority}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.region', 'Region')}</label>
                <input
                  type="text"
                  value={machineForm.region}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, region: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.provider', 'Provider')}</label>
                <input
                  type="text"
                  value={machineForm.provider}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, provider: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.notes', 'Notes')}</label>
              <textarea
                value={machineForm.notes}
                onChange={(e) => setMachineForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                rows={2}
              />
            </div>
            {/* Update Credentials Section */}
            <div className="pt-4 mt-4" style={{ borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: tableBorderColor }}>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: titleColor }}>
                <Key size={16} style={{ color: '#818CF8' }} />
                {t('superAdmin.anbox.form.updateCredentials', 'Update Credentials')}
              </h4>
              <p className="text-xs mb-3" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.credentialsHint', 'Leave blank to keep existing credentials')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.rootUsername', 'Root Username')}</label>
                  <input
                    type="text"
                    value={machineForm.root_username || ''}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, root_username: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                    placeholder="Leave blank to keep existing"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.rootPassword', 'Root Password')}</label>
                  <input
                    type="password"
                    value={machineForm.root_password || ''}
                    onChange={(e) => setMachineForm(prev => ({ ...prev, root_password: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                    placeholder="Leave blank to keep existing"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.authToken', 'Auth Token')}</label>
                <input
                  type="password"
                  value={machineForm.auth_token || ''}
                  onChange={(e) => setMachineForm(prev => ({ ...prev, auth_token: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                  placeholder="Leave blank to keep existing"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => { setShowEditMachineModal(false); setSelectedMachine(null); resetMachineForm(); }}
              className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
              style={{ background: buttonBg, color: buttonText }}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleUpdateMachine}
              disabled={actionLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-500 rounded-md hover:bg-indigo-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading && <Loader2 size={14} className="animate-spin" />}
              {t('common.save', 'Save Changes')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render Delete Machine Modal
  const renderDeleteMachineModal = () => {
    if (!showDeleteMachineModal || !selectedMachine) return null;
    return ReactDOM.createPortal(
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
        <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}>
              <AlertTriangle size={20} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
            </div>
            <h3 className="text-lg font-semibold" style={{ color: titleColor }}>{t('superAdmin.anbox.deleteMachine', 'Delete Machine')}</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: subtitleColor }}>
            {t('superAdmin.anbox.deleteMachineConfirm', 'Are you sure you want to delete machine')} <strong style={{ color: titleColor }}>{selectedMachine.name}</strong>?
          </p>
          {(selectedMachine.assigned_instances || 0) > 0 && (
            <div className="rounded-lg p-3 mb-4 text-sm flex items-center gap-2" style={{ background: isDark ? '#2A2008' : '#FFFBEB', borderColor: isDark ? '#3D3010' : '#FEF3C7', borderWidth: 1, borderStyle: 'solid', color: isDark ? '#FACC15' : '#A16207' }}>
              <AlertTriangle size={14} />
              {t('superAdmin.anbox.machineHasInstances', 'This machine has assigned instances. Release them first.')}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => { setShowDeleteMachineModal(false); setSelectedMachine(null); }}
              className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
              style={{ background: buttonBg, color: buttonText }}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => handleDeleteMachine(false)}
              disabled={actionLoading || (selectedMachine.assigned_instances || 0) > 0}
              className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading && <Loader2 size={14} className="animate-spin" />}
              {t('common.delete', 'Delete')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render Add Instances Modal
  const renderAddInstancesModal = () => {
    if (!showAddInstancesModal || !selectedMachine) return null;
    return ReactDOM.createPortal(
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
        <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: titleColor }}>{t('superAdmin.anbox.addInstancesToMachine', 'Add Instances')}</h3>
            <button onClick={() => { setShowAddInstancesModal(false); setSelectedMachine(null); }} style={{ color: subtitleColor }}>
              <X size={18} />
            </button>
          </div>
          <p className="text-sm mb-4" style={{ color: subtitleColor }}>
            {t('superAdmin.anbox.addInstancesToMachineDesc', 'Add new instance slots to')} <strong style={{ color: titleColor }}>{selectedMachine.name}</strong>
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.count', 'Number of Instances')}</label>
              <input
                type="number"
                value={addInstancesCount}
                onChange={(e) => setAddInstancesCount(parseInt(e.target.value) || 1)}
                min={1}
                max={100}
                className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.instancePrefix', 'Instance Prefix')}</label>
              <input
                type="text"
                value={addInstancesPrefix}
                onChange={(e) => setAddInstancesPrefix(e.target.value)}
                className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                placeholder="instance"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => { setShowAddInstancesModal(false); setSelectedMachine(null); }}
              className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
              style={{ background: buttonBg, color: buttonText }}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleAddInstances}
              disabled={actionLoading || addInstancesCount < 1}
              className="px-4 py-2 text-sm font-medium text-white bg-green-500 rounded-md hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading && <Loader2 size={14} className="animate-spin" />}
              {t('superAdmin.anbox.addInstances', 'Add Instances')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render Instance Action Modal
  const renderInstanceActionModal = () => {
    if (!showInstanceActionModal || !selectedInstance) return null;

    const getActionIconStyle = (): { bg: string; iconColor: string } => {
      if (instanceAction === 'delete') return { bg: isDark ? '#2A1215' : '#FEE2E2', iconColor: isDark ? '#F87171' : '#DC2626' };
      if (instanceAction === 'release') return { bg: isDark ? '#2A2008' : '#FFEDD5', iconColor: isDark ? '#FB923C' : '#EA580C' };
      return { bg: isDark ? '#0C1A2E' : '#EEF2FF', iconColor: isDark ? '#818CF8' : '#4338CA' };
    };
    const actionStyle = getActionIconStyle();

    return ReactDOM.createPortal(
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
        <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: actionStyle.bg }}>
              {instanceAction === 'delete' ? <Trash2 size={20} style={{ color: actionStyle.iconColor }} /> :
               instanceAction === 'release' ? <ArrowRightFromLine size={20} style={{ color: actionStyle.iconColor }} /> :
               <RefreshCcw size={20} style={{ color: actionStyle.iconColor }} />}
            </div>
            <h3 className="text-lg font-semibold" style={{ color: titleColor }}>
              {instanceAction === 'delete' ? t('superAdmin.anbox.deleteInstance', 'Delete Instance') :
               instanceAction === 'release' ? t('superAdmin.anbox.releaseInstance', 'Release Instance') :
               t('superAdmin.anbox.changeInstanceStatus', 'Change Instance Status')}
            </h3>
          </div>

          {instanceAction === 'status' ? (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: subtitleColor }}>{t('superAdmin.anbox.selectStatus', 'Select Status')}</label>
              <select
                value={newInstanceStatus}
                onChange={(e) => setNewInstanceStatus(e.target.value)}
                className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
              >
                <option value="available">Available</option>
                <option value="assigned">Assigned</option>
                <option value="starting">Starting</option>
                <option value="running">Running</option>
                <option value="stopping">Stopping</option>
                <option value="stopped">Stopped</option>
                <option value="error">Error</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
          ) : (
            <p className="text-sm mb-4" style={{ color: subtitleColor }}>
              {instanceAction === 'delete'
                ? t('superAdmin.anbox.deleteInstanceConfirm', 'Are you sure you want to delete instance')
                : t('superAdmin.anbox.releaseInstanceConfirm', 'Are you sure you want to release instance')
              } <strong style={{ color: titleColor }}>{selectedInstance.instance_name}</strong>?
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={() => { setShowInstanceActionModal(false); setSelectedInstance(null); }}
              className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
              style={{ background: buttonBg, color: buttonText }}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleInstanceAction}
              disabled={actionLoading}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 flex items-center gap-2 ${
                instanceAction === 'delete' ? 'bg-red-500 hover:bg-red-600' :
                instanceAction === 'release' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-indigo-500 hover:bg-indigo-600'
              }`}
            >
              {actionLoading && <Loader2 size={14} className="animate-spin" />}
              {instanceAction === 'delete' ? t('common.delete', 'Delete') :
               instanceAction === 'release' ? t('superAdmin.anbox.release', 'Release') :
               t('common.save', 'Save')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render Credentials Modal
  const renderCredentialsModal = () => {
    if (!showCredentialsModal || !selectedMachine) return null;
    return ReactDOM.createPortal(
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
        <div className="rounded-lg p-6 max-w-lg w-full mx-4" style={{ background: modalBg }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#2A2008' : '#FEF3C7' }}>
                <Key size={20} style={{ color: isDark ? '#FACC15' : '#A16207' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: titleColor }}>{t('superAdmin.anbox.machineCredentials', 'Machine Credentials')}</h3>
                <p className="text-sm" style={{ color: subtitleColor }}>{selectedMachine.name}</p>
              </div>
            </div>
            <button
              onClick={() => { setShowCredentialsModal(false); setSelectedMachine(null); setMachineCredentials(null); setShowPasswords(false); }}
              style={{ color: subtitleColor }}
            >
              <X size={18} />
            </button>
          </div>

          {credentialsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="large" text={t('superAdmin.loading', 'Loading...')} />
            </div>
          ) : machineCredentials ? (
            <div className="space-y-4">
              {/* Warning Banner */}
              <div className="rounded-lg p-3 flex items-start gap-2" style={{ background: isDark ? '#2A2008' : '#FFFBEB', borderColor: isDark ? '#3D3010' : '#FEF3C7', borderWidth: 1, borderStyle: 'solid' }}>
                <AlertTriangle size={14} className="mt-0.5" style={{ color: isDark ? '#FACC15' : '#A16207' }} />
                <p className="text-xs" style={{ color: isDark ? '#FACC15' : '#A16207' }}>
                  {t('superAdmin.anbox.credentialsWarning', 'These credentials are sensitive. Do not share them with unauthorized users.')}
                </p>
              </div>

              {/* Connection Info */}
              <div className="rounded-lg p-4 space-y-3" style={{ background: modalInfoBg }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.host', 'Host')}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono" style={{ color: titleColor }}>{machineCredentials.host}:{machineCredentials.port}</span>
                    <button
                      onClick={() => copyToClipboard(`${machineCredentials.host}:${machineCredentials.port}`, 'Host')}
                      className="p-1 transition-colors"
                      style={{ color: subtitleColor }}
                      title="Copy"
                    >
                      <Clipboard size={12} />
                    </button>
                  </div>
                </div>
                {machineCredentials.api_endpoint && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.apiEndpoint', 'API Endpoint')}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono truncate max-w-[200px]" style={{ color: titleColor }} title={machineCredentials.api_endpoint}>{machineCredentials.api_endpoint}</span>
                      <button
                        onClick={() => copyToClipboard(machineCredentials.api_endpoint!, 'API Endpoint')}
                        className="p-1 transition-colors"
                        style={{ color: subtitleColor }}
                        title="Copy"
                      >
                        <Clipboard size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Credentials */}
              <div className="rounded-lg p-4 space-y-3" style={{ background: modalInfoBg }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold" style={{ color: titleColor }}>{t('superAdmin.anbox.form.credentials', 'Credentials')}</span>
                  <button
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="text-xs flex items-center gap-1"
                    style={{ color: isDark ? '#818CF8' : '#4F46E5' }}
                  >
                    {showPasswords ? <EyeOff size={12} /> : <Eye size={12} />}
                    {showPasswords ? t('common.hide', 'Hide') : t('common.show', 'Show')}
                  </button>
                </div>

                {machineCredentials.root_username && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.rootUsername', 'Username')}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono" style={{ color: titleColor }}>
                        {showPasswords ? machineCredentials.root_username : '••••••••'}
                      </span>
                      <button
                        onClick={() => copyToClipboard(machineCredentials.root_username!, 'Username')}
                        className="p-1 transition-colors"
                        style={{ color: subtitleColor }}
                        title="Copy"
                      >
                        <Clipboard size={12} />
                      </button>
                    </div>
                  </div>
                )}

                {machineCredentials.root_password && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.rootPassword', 'Password')}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono" style={{ color: titleColor }}>
                        {showPasswords ? machineCredentials.root_password : '••••••••'}
                      </span>
                      <button
                        onClick={() => copyToClipboard(machineCredentials.root_password!, 'Password')}
                        className="p-1 transition-colors"
                        style={{ color: subtitleColor }}
                        title="Copy"
                      >
                        <Clipboard size={12} />
                      </button>
                    </div>
                  </div>
                )}

                {machineCredentials.auth_token && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium" style={{ color: subtitleColor }}>{t('superAdmin.anbox.form.authToken', 'Auth Token')}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono truncate max-w-[150px]" style={{ color: titleColor }}>
                        {showPasswords ? machineCredentials.auth_token : '••••••••••••'}
                      </span>
                      <button
                        onClick={() => copyToClipboard(machineCredentials.auth_token!, 'Auth Token')}
                        className="p-1 transition-colors"
                        style={{ color: subtitleColor }}
                        title="Copy"
                      >
                        <Clipboard size={12} />
                      </button>
                    </div>
                  </div>
                )}

                {!machineCredentials.root_username && !machineCredentials.root_password && !machineCredentials.auth_token && (
                  <p className="text-sm text-center py-2" style={{ color: subtitleColor }}>
                    {t('superAdmin.anbox.noCredentialsSet', 'No credentials have been set for this machine')}
                  </p>
                )}
              </div>

              {/* SSH Command */}
              {machineCredentials.root_username && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-400">{t('superAdmin.anbox.sshCommand', 'SSH Command')}</span>
                    <button
                      onClick={() => copyToClipboard(`ssh ${machineCredentials.root_username}@${machineCredentials.host}`, 'SSH Command')}
                      className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                    >
                      <Clipboard size={12} />
                      {t('common.copy', 'Copy')}
                    </button>
                  </div>
                  <code className="text-sm text-green-400 font-mono">
                    ssh {machineCredentials.root_username}@{machineCredentials.host}
                  </code>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8" style={{ color: subtitleColor }}>
              {t('superAdmin.anbox.failedToLoadCredentials', 'Failed to load credentials')}
            </div>
          )}

          <div className="flex justify-end mt-6">
            <button
              onClick={() => { setShowCredentialsModal(false); setSelectedMachine(null); setMachineCredentials(null); setShowPasswords(false); }}
              className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
              style={{ background: buttonBg, color: buttonText }}
            >
              {t('common.close', 'Close')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ gap: '8px', background: containerBg }}>
      {/* Main Card */}
      <div className="flex flex-col items-start flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl" style={{ background: mainBg, boxShadow: mainShadow }}>
        {/* Header */}
        <div className="flex flex-col items-start w-full flex-shrink-0 rounded-none lg:rounded-t-2xl" style={{ padding: '12px 16px', gap: '8px', borderBottom: `1px solid ${headerBorder}` }}>
          {/* Row 1: hamburger + icon + title */}
          <div className="flex flex-row items-center w-full" style={{ gap: '8px' }}>
            {/* Hamburger menu for mobile */}
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            {/* Icon */}
            <div className="hidden lg:flex" style={{ width: '36px', height: '36px', background: isDark ? '#1C398E' : '#EFF6FF', borderRadius: '12px', alignItems: 'center', justifyContent: 'center' }}>
              <Cloud size={20} style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }} />
            </div>
            {/* Title */}
            <h1 style={{ fontFamily: 'Geist, sans-serif', fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, margin: 0, whiteSpace: 'nowrap' }}>Anbox Cloud</h1>
          </div>
          {/* Row 2: tabs + action buttons - scrollable */}
          <div className="w-full overflow-x-auto">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}>
              {/* Segmented Tab Control */}
              <div style={{ display: 'flex', border: `1px solid ${headerBorder}`, borderRadius: '8px', overflow: 'hidden', height: '36px' }}>
                <button
                  onClick={() => setActiveTab('stats')}
                  style={{
                    padding: '0 12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    background: activeTab === 'stats' ? (isDark ? '#1C398E' : '#EFF6FF') : 'transparent',
                    color: activeTab === 'stats' ? (isDark ? '#51A2FF' : '#2B7FFF') : subtitleColor
                  }}
                >
                  {t('superAdmin.anbox.tabs.stats', 'Statistics')}
                </button>
                <button
                  onClick={() => setActiveTab('machines')}
                  style={{
                    padding: '0 12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    background: activeTab === 'machines' ? (isDark ? '#1C398E' : '#EFF6FF') : 'transparent',
                    color: activeTab === 'machines' ? (isDark ? '#51A2FF' : '#2B7FFF') : subtitleColor
                  }}
                >
                  {t('superAdmin.anbox.tabs.machines', 'Machines')}
                </button>
                <button
                  onClick={() => setActiveTab('instances')}
                  style={{
                    padding: '0 12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    background: activeTab === 'instances' ? (isDark ? '#1C398E' : '#EFF6FF') : 'transparent',
                    color: activeTab === 'instances' ? (isDark ? '#51A2FF' : '#2B7FFF') : subtitleColor
                  }}
                >
                  {t('superAdmin.anbox.tabs.instances', 'Instances')}
                </button>
              </div>
              {/* Refresh button */}
              <button
                onClick={() => {
                  if (activeTab === 'stats') fetchStats();
                  else if (activeTab === 'machines') fetchMachines();
                  else fetchInstances();
                }}
                className="flex items-center gap-2 transition-colors"
                style={{ padding: '0 12px', height: '36px', fontSize: '14px', fontWeight: 500, borderRadius: '8px', background: buttonBg, color: buttonText, border: 'none', cursor: 'pointer' }}
              >
                <RefreshCcw size={14} />
                {t('common.refresh', 'Refresh')}
              </button>
              {/* Add Machine button (only on machines tab) */}
              {activeTab === 'machines' && (
                <button
                  onClick={() => setShowAddMachineModal(true)}
                  className="flex items-center gap-2 bg-indigo-500 text-white hover:bg-indigo-600 transition-colors"
                  style={{ padding: '0 12px', height: '36px', fontSize: '14px', fontWeight: 500, borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                >
                  <Plus size={14} />
                  {t('superAdmin.anbox.addMachine', 'Add Machine')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', flex: 1, minHeight: 0 }}>
          {/* Messages */}
          {successMessage && (
            <div className="p-3 rounded-lg text-sm flex items-center gap-2" style={{ background: successBg, borderWidth: 1, borderStyle: 'solid', borderColor: successBorder, color: successText }}>
              <CheckCircle2 size={16} />
              {successMessage}
            </div>
          )}
          {error && (
            <div className="p-3 rounded-lg text-sm flex items-center gap-2" style={{ background: errorBg, borderWidth: 1, borderStyle: 'solid', borderColor: errorBorder, color: errorText }}>
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Spinner size="large" text={t('superAdmin.loading', 'Loading...')} />
            </div>
          )}

          {/* Stats Tab - No Data */}
          {!loading && activeTab === 'stats' && !stats && !error && (
            <div className="p-8 text-center">
              <Cloud size={48} style={{ color: subtitleColor }} className="mx-auto mb-3" />
              <p style={{ color: subtitleColor }}>{t('superAdmin.anbox.noStatsAvailable', 'No statistics available')}</p>
              <button
                onClick={fetchStats}
                className="mt-4 px-4 py-2 bg-indigo-500 text-white text-sm font-medium rounded-md hover:bg-indigo-600 transition-colors"
              >
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          {/* Stats Tab */}
          {!loading && activeTab === 'stats' && stats && (
            <>
              {/* Machines Overview */}
              <div>
                <h2 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: titleColor }}>
                  <HardDrive size={16} style={{ color: isDark ? '#818CF8' : '#4F46E5' }} />
                  {t('superAdmin.anbox.machinesOverview', 'Machines Overview')}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {/* Total */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#0C1A2E' : '#EEF2FF', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#162A4A' : '#C7D2FE' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#818CF8' : '#4338CA' }}>{t('superAdmin.anbox.stats.totalMachines', 'Total')}</span>
                      <HardDrive size={16} style={{ color: isDark ? '#818CF8' : '#4F46E5' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#818CF8' : '#4338CA' }}>{formatNumber(stats.machines.total)}</div>
                  </div>

                  {/* Active */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#0D2818' : '#F0FDF4', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#1A4028' : '#BBF7D0' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#5EE9B5' : '#15803D' }}>{t('superAdmin.anbox.stats.active', 'Active')}</span>
                      <CheckCircle2 size={16} style={{ color: isDark ? '#5EE9B5' : '#16A34A' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#5EE9B5' : '#15803D' }}>{formatNumber(stats.machines.active)}</div>
                  </div>

                  {/* Maintenance */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#2A2008' : '#FEFCE8', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#3D3010' : '#FEF08A' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#FACC15' : '#A16207' }}>{t('superAdmin.anbox.stats.maintenance', 'Maintenance')}</span>
                      <Wrench size={16} style={{ color: isDark ? '#FACC15' : '#CA8A04' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#FACC15' : '#A16207' }}>{formatNumber(stats.machines.maintenance)}</div>
                  </div>

                  {/* Offline */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#1D293D' : '#F9FAFB', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#314158' : '#E5E7EB' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#90A1B9' : '#475569' }}>{t('superAdmin.anbox.stats.offline', 'Offline')}</span>
                      <XCircle size={16} style={{ color: isDark ? '#90A1B9' : '#6B7280' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#90A1B9' : '#475569' }}>{formatNumber(stats.machines.offline)}</div>
                  </div>

                  {/* Total Capacity */}
                  <div className="rounded-lg p-4" style={{ background: isDark ? '#150D28' : '#FAF5FF', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#2A1A4A' : '#E9D5FF' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium" style={{ color: isDark ? '#C084FC' : '#7E22CE' }}>{t('superAdmin.anbox.stats.totalCapacity', 'Total Capacity')}</span>
                      <BarChart3 size={16} style={{ color: isDark ? '#C084FC' : '#9333EA' }} />
                    </div>
                    <div className="text-xl font-bold" style={{ color: isDark ? '#C084FC' : '#7E22CE' }}>{formatNumber(stats.machines.total_capacity)}</div>
                    <div className="text-xs mt-1" style={{ color: isDark ? '#A855F7' : '#9333EA' }}>
                      {formatNumber((stats.machines.total_capacity || 0) - (stats.instances.total || 0))} {t('superAdmin.anbox.free', 'free')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Instances Overview */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: titleColor }}>
                    <Box size={16} style={{ color: isDark ? '#818CF8' : '#4F46E5' }} />
                    {t('superAdmin.anbox.instancesOverview', 'Instances Overview')}
                  </h2>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color: isDark ? '#818CF8' : '#4F46E5' }}>{stats.instances.utilization_percentage.toFixed(1)}%</p>
                      <p className="text-xs" style={{ color: subtitleColor }}>{t('superAdmin.anbox.stats.utilization', 'Utilization')}</p>
                    </div>
                  </div>
                </div>

                {/* Utilization Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-1" style={{ color: subtitleColor }}>
                    <span>{t('superAdmin.anbox.stats.used', 'Used')}: {formatNumber(stats.instances.used)}</span>
                    <span>{t('superAdmin.anbox.stats.total', 'Total')}: {formatNumber(stats.instances.total)}</span>
                  </div>
                  <div className="w-full rounded-full h-3" style={{ background: isDark ? '#314158' : '#E5E7EB' }}>
                    <div
                      className={`h-3 rounded-full transition-all ${
                        stats.instances.utilization_percentage > 90 ? 'bg-red-500' :
                        stats.instances.utilization_percentage > 75 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(stats.instances.utilization_percentage, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Instance Status Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                  {/* Available - Green */}
                  <div className="rounded-lg p-3 text-center" style={{ background: isDark ? '#0D2818' : '#F0FDF4', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#1A4028' : '#BBF7D0' }}>
                    <div className="text-lg font-bold" style={{ color: isDark ? '#5EE9B5' : '#15803D' }}>{formatNumber(stats.instances.by_status.available)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#34D399' : '#16A34A' }}>{t('superAdmin.anbox.status.available', 'Available')}</div>
                  </div>
                  {/* Assigned - Blue */}
                  <div className="rounded-lg p-3 text-center" style={{ background: isDark ? '#0C1A2E' : '#EFF6FF', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#162A4A' : '#BFDBFE' }}>
                    <div className="text-lg font-bold" style={{ color: isDark ? '#60A5FA' : '#1D4ED8' }}>{formatNumber(stats.instances.by_status.assigned)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#3B82F6' : '#2563EB' }}>{t('superAdmin.anbox.status.assigned', 'Assigned')}</div>
                  </div>
                  {/* Running - Emerald */}
                  <div className="rounded-lg p-3 text-center" style={{ background: isDark ? '#0D2818' : '#ECFDF5', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#1A4028' : '#A7F3D0' }}>
                    <div className="text-lg font-bold" style={{ color: isDark ? '#34D399' : '#047857' }}>{formatNumber(stats.instances.by_status.running)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#10B981' : '#059669' }}>{t('superAdmin.anbox.status.running', 'Running')}</div>
                  </div>
                  {/* Starting - Cyan */}
                  <div className="rounded-lg p-3 text-center" style={{ background: isDark ? '#0B1D2E' : '#ECFEFF', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#143D5A' : '#A5F3FC' }}>
                    <div className="text-lg font-bold" style={{ color: isDark ? '#22D3EE' : '#0E7490' }}>{formatNumber(stats.instances.by_status.starting)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#06B6D4' : '#0891B2' }}>{t('superAdmin.anbox.status.starting', 'Starting')}</div>
                  </div>
                  {/* Stopping - Orange */}
                  <div className="rounded-lg p-3 text-center" style={{ background: isDark ? '#2A1A08' : '#FFF7ED', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#3D2810' : '#FED7AA' }}>
                    <div className="text-lg font-bold" style={{ color: isDark ? '#FB923C' : '#C2410C' }}>{formatNumber(stats.instances.by_status.stopping)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#F97316' : '#EA580C' }}>{t('superAdmin.anbox.status.stopping', 'Stopping')}</div>
                  </div>
                  {/* Stopped - Gray */}
                  <div className="rounded-lg p-3 text-center" style={{ background: isDark ? '#1D293D' : '#F9FAFB', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#314158' : '#E5E7EB' }}>
                    <div className="text-lg font-bold" style={{ color: isDark ? '#90A1B9' : '#475569' }}>{formatNumber(stats.instances.by_status.stopped)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#64748B' : '#6B7280' }}>{t('superAdmin.anbox.status.stopped', 'Stopped')}</div>
                  </div>
                  {/* Error - Red */}
                  <div className="rounded-lg p-3 text-center" style={{ background: isDark ? '#2A1215' : '#FEF2F2', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#4A1C20' : '#FECACA' }}>
                    <div className="text-lg font-bold" style={{ color: isDark ? '#F87171' : '#DC2626' }}>{formatNumber(stats.instances.by_status.error)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#EF4444' : '#EF4444' }}>{t('superAdmin.anbox.status.error', 'Error')}</div>
                  </div>
                  {/* Maintenance - Yellow */}
                  <div className="rounded-lg p-3 text-center" style={{ background: isDark ? '#2A2008' : '#FEFCE8', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#3D3010' : '#FEF08A' }}>
                    <div className="text-lg font-bold" style={{ color: isDark ? '#FACC15' : '#A16207' }}>{formatNumber(stats.instances.by_status.maintenance)}</div>
                    <div className="text-xs" style={{ color: isDark ? '#EAB308' : '#CA8A04' }}>{t('superAdmin.anbox.status.maintenance', 'Maintenance')}</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Machines Tab */}
          {!loading && activeTab === 'machines' && (
            <>
              {/* Filters */}
              <div className="flex flex-wrap gap-3">
                <select
                  value={machineStatusFilter}
                  onChange={(e) => {
                    setMachineStatusFilter(e.target.value);
                    setMachinesPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                >
                  <option value="">{t('superAdmin.anbox.filters.allStatus', 'All Status')}</option>
                  <option value="active">{t('superAdmin.anbox.status.active', 'Active')}</option>
                  <option value="maintenance">{t('superAdmin.anbox.status.maintenance', 'Maintenance')}</option>
                  <option value="offline">{t('superAdmin.anbox.status.offline', 'Offline')}</option>
                </select>
              </div>

              {/* Machines Table */}
              <div className={`rounded-lg overflow-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ borderWidth: 1, borderStyle: 'solid', borderColor: headerBorder }}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead style={{ background: tableHeaderBg, borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.machine', 'Machine')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.host', 'Host')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.status', 'Status')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.capacity', 'Capacity')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.instances', 'Instances')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.utilization', 'Utilization')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {machines.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center" style={{ color: subtitleColor }}>
                            <Inbox size={32} className="mx-auto mb-2" style={{ color: subtitleColor }} />
                            {t('superAdmin.anbox.noMachines', 'No machines found')}
                          </td>
                        </tr>
                      ) : (
                        machines.map((machine) => (
                          <tr key={machine._id} style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: isDark ? '#0C1A2E' : '#EEF2FF' }}>
                                  <HardDrive size={14} style={{ color: isDark ? '#818CF8' : '#4F46E5' }} />
                                </div>
                                <div>
                                  <span className="text-sm font-medium" style={{ color: titleColor }}>{machine.name}</span>
                                  {machine.region && (
                                    <p className="text-xs" style={{ color: subtitleColor }}>{machine.region}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm" style={{ color: subtitleColor }}>{machine.host}:{machine.port}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 text-xs font-medium rounded-full capitalize" style={getStatusBadgeStyle(machine.status)}>
                                {machine.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm">
                                <span className="font-medium" style={{ color: titleColor }}>{formatNumber(machine.total_capacity)}</span>
                                <span style={{ color: subtitleColor }} className="mx-1">|</span>
                                <span style={{ color: isDark ? '#60A5FA' : '#2563EB' }} title={t('superAdmin.anbox.remainingSlots', 'Remaining slots')}>
                                  {formatNumber((machine.total_capacity || 0) - (machine.total_instances || 0))} {t('superAdmin.anbox.free', 'free')}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm">
                                <span style={{ color: isDark ? '#5EE9B5' : '#16A34A' }}>{formatNumber(machine.available_instances)}</span>
                                <span style={{ color: subtitleColor }}> / </span>
                                <span style={{ color: subtitleColor }}>{formatNumber(machine.total_instances)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-16 rounded-full h-2" style={{ background: isDark ? '#314158' : '#E5E7EB' }}>
                                  <div
                                    className={`h-2 rounded-full ${
                                      (machine.utilization_percentage || 0) > 90 ? 'bg-red-500' :
                                      (machine.utilization_percentage || 0) > 75 ? 'bg-yellow-500' : 'bg-green-500'
                                    }`}
                                    style={{ width: `${Math.min(machine.utilization_percentage || 0, 100)}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs" style={{ color: subtitleColor }}>{(machine.utilization_percentage || 0).toFixed(0)}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleViewCredentials(machine)}
                                  className="p-1.5 transition-colors"
                                  style={{ color: subtitleColor }}
                                  title={t('superAdmin.anbox.viewCredentials', 'View Credentials')}
                                >
                                  <Key size={14} />
                                </button>
                                <button
                                  onClick={() => openEditMachineModal(machine)}
                                  className="p-1.5 transition-colors"
                                  style={{ color: subtitleColor }}
                                  title={t('common.edit', 'Edit')}
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedMachine(machine);
                                    setShowAddInstancesModal(true);
                                  }}
                                  className="p-1.5 transition-colors"
                                  style={{ color: subtitleColor }}
                                  title={t('superAdmin.anbox.addInstances', 'Add Instances')}
                                >
                                  <PlusCircle size={14} />
                                </button>
                                <select
                                  value={machine.status}
                                  onChange={(e) => handleUpdateMachineStatus(machine._id, e.target.value as any)}
                                  className="text-xs px-2 py-1 rounded-md"
                                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                                  disabled={actionLoading}
                                >
                                  <option value="active">Active</option>
                                  <option value="maintenance">Maintenance</option>
                                  <option value="offline">Offline</option>
                                </select>
                                <button
                                  onClick={() => {
                                    setSelectedMachine(machine);
                                    setShowDeleteMachineModal(true);
                                  }}
                                  className="p-1.5 transition-colors"
                                  style={{ color: subtitleColor }}
                                  title={t('common.delete', 'Delete')}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {machines.length > 0 && (
                  <div className="px-4 py-3 flex items-center justify-between" style={{ borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: tableBorderColor }}>
                    <div className="text-sm" style={{ color: subtitleColor }}>
                      {t('superAdmin.pagination.showing', 'Showing')} {((machinesPagination.page - 1) * machinesPagination.limit) + 1} - {Math.min(machinesPagination.page * machinesPagination.limit, machinesPagination.total)} {t('superAdmin.pagination.of', 'of')} {machinesPagination.total}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setMachinesPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={machinesPagination.page === 1}
                        className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: paginationBg, borderColor: paginationBorder, color: paginationText, borderWidth: 1, borderStyle: 'solid' }}
                      >
                        {t('common.previous', 'Previous')}
                      </button>
                      <button
                        onClick={() => setMachinesPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={machinesPagination.page >= machinesPagination.totalPages}
                        className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: paginationBg, borderColor: paginationBorder, color: paginationText, borderWidth: 1, borderStyle: 'solid' }}
                      >
                        {t('common.next', 'Next')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Instances Tab */}
          {!loading && activeTab === 'instances' && (
            <>
              {/* Filters */}
              <div className="flex flex-wrap gap-3">
                <select
                  value={instanceStatusFilter}
                  onChange={(e) => {
                    setInstanceStatusFilter(e.target.value);
                    setInstancesPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                >
                  <option value="">{t('superAdmin.anbox.filters.allStatus', 'All Status')}</option>
                  <option value="available">{t('superAdmin.anbox.status.available', 'Available')}</option>
                  <option value="assigned">{t('superAdmin.anbox.status.assigned', 'Assigned')}</option>
                  <option value="running">{t('superAdmin.anbox.status.running', 'Running')}</option>
                  <option value="starting">{t('superAdmin.anbox.status.starting', 'Starting')}</option>
                  <option value="stopping">{t('superAdmin.anbox.status.stopping', 'Stopping')}</option>
                  <option value="stopped">{t('superAdmin.anbox.status.stopped', 'Stopped')}</option>
                  <option value="error">{t('superAdmin.anbox.status.error', 'Error')}</option>
                  <option value="maintenance">{t('superAdmin.anbox.status.maintenance', 'Maintenance')}</option>
                </select>
                <select
                  value={instanceMachineFilter}
                  onChange={(e) => {
                    setInstanceMachineFilter(e.target.value);
                    setInstancesPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{ background: inputBg, borderColor: inputBorder, color: inputText, borderWidth: 1, borderStyle: 'solid' }}
                >
                  <option value="">{t('superAdmin.anbox.filters.allMachines', 'All Machines')}</option>
                  {machines.map(m => (
                    <option key={m._id} value={m._id}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Instances Table */}
              <div className={`rounded-lg overflow-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ borderWidth: 1, borderStyle: 'solid', borderColor: headerBorder }}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead style={{ background: tableHeaderBg, borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.instance', 'Instance')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.machine', 'Machine')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.status', 'Status')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.account', 'Account')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.user', 'User')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.metrics', 'Metrics')}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.anbox.table.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instances.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center" style={{ color: subtitleColor }}>
                            <Inbox size={32} className="mx-auto mb-2" style={{ color: subtitleColor }} />
                            {t('superAdmin.anbox.noInstances', 'No instances found')}
                          </td>
                        </tr>
                      ) : (
                        instances.map((instance) => (
                          <tr key={instance._id} style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: isDark ? '#1D293D' : '#F3F4F6' }}>
                                  <Box size={14} style={{ color: subtitleColor }} />
                                </div>
                                <div>
                                  <span className="text-sm font-medium" style={{ color: titleColor }}>{instance.instance_name}</span>
                                  <p className="text-xs" style={{ color: subtitleColor }}>Slot #{instance.slot_number}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm" style={{ color: subtitleColor }}>{instance.machine.name}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 text-xs font-medium rounded-full capitalize" style={getStatusBadgeStyle(instance.status)}>
                                {instance.status}
                              </span>
                              {instance.error_message && (
                                <p className="text-xs mt-1 truncate max-w-[150px]" style={{ color: isDark ? '#F87171' : '#EF4444' }} title={instance.error_message}>
                                  {instance.error_message}
                                </p>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {instance.account ? (
                                <div>
                                  <span className="text-sm font-medium" style={{ color: titleColor }}>@{instance.account.username}</span>
                                  <p className="text-xs capitalize" style={{ color: subtitleColor }}>{instance.account.platform}</p>
                                </div>
                              ) : (
                                <span className="text-xs" style={{ color: subtitleColor }}>-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {instance.user ? (
                                <div>
                                  <span className="text-sm" style={{ color: titleColor }}>{instance.user.fullName || instance.user.email}</span>
                                  {instance.user.fullName && (
                                    <p className="text-xs" style={{ color: subtitleColor }}>{instance.user.email}</p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs" style={{ color: subtitleColor }}>-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {(instance.cpu_usage !== undefined || instance.memory_usage !== undefined) ? (
                                <div className="text-xs">
                                  <div className="flex items-center gap-1">
                                    <span style={{ color: subtitleColor }}>CPU:</span>
                                    <span style={{ color: instance.cpu_usage && instance.cpu_usage > 80 ? (isDark ? '#F87171' : '#DC2626') : (isDark ? '#E2E8F0' : '#374151') }}>
                                      {instance.cpu_usage?.toFixed(0) || 0}%
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span style={{ color: subtitleColor }}>RAM:</span>
                                    <span style={{ color: instance.memory_usage && instance.memory_usage > 80 ? (isDark ? '#F87171' : '#DC2626') : (isDark ? '#E2E8F0' : '#374151') }}>
                                      {instance.memory_usage?.toFixed(0) || 0}%
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-xs" style={{ color: subtitleColor }}>-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                {instance.account && (
                                  <button
                                    onClick={() => {
                                      setSelectedInstance(instance);
                                      setInstanceAction('release');
                                      setShowInstanceActionModal(true);
                                    }}
                                    className="p-1.5 transition-colors"
                                    style={{ color: subtitleColor }}
                                    title={t('superAdmin.anbox.releaseInstance', 'Release Instance')}
                                  >
                                    <ArrowRightFromLine size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setSelectedInstance(instance);
                                    setNewInstanceStatus(instance.status);
                                    setInstanceAction('status');
                                    setShowInstanceActionModal(true);
                                  }}
                                  className="p-1.5 transition-colors"
                                  style={{ color: subtitleColor }}
                                  title={t('superAdmin.anbox.changeStatus', 'Change Status')}
                                >
                                  <RefreshCcw size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedInstance(instance);
                                    setInstanceAction('delete');
                                    setShowInstanceActionModal(true);
                                  }}
                                  className="p-1.5 transition-colors"
                                  style={{ color: subtitleColor }}
                                  title={t('common.delete', 'Delete')}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {instances.length > 0 && (
                  <div className="px-4 py-3 flex items-center justify-between" style={{ borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: tableBorderColor }}>
                    <div className="text-sm" style={{ color: subtitleColor }}>
                      {t('superAdmin.pagination.showing', 'Showing')} {((instancesPagination.page - 1) * instancesPagination.limit) + 1} - {Math.min(instancesPagination.page * instancesPagination.limit, instancesPagination.total)} {t('superAdmin.pagination.of', 'of')} {instancesPagination.total}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setInstancesPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={instancesPagination.page === 1}
                        className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: paginationBg, borderColor: paginationBorder, color: paginationText, borderWidth: 1, borderStyle: 'solid' }}
                      >
                        {t('common.previous', 'Previous')}
                      </button>
                      <button
                        onClick={() => setInstancesPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={instancesPagination.page >= instancesPagination.totalPages}
                        className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: paginationBg, borderColor: paginationBorder, color: paginationText, borderWidth: 1, borderStyle: 'solid' }}
                      >
                        {t('common.next', 'Next')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {renderAddMachineModal()}
      {renderEditMachineModal()}
      {renderDeleteMachineModal()}
      {renderAddInstancesModal()}
      {renderInstanceActionModal()}
      {renderCredentialsModal()}
    </div>
  );
}
